import { inject, Injectable } from '@angular/core';

import { WalletIdentityStore } from '../store';
import { REOWN_APPKIT_ACCESS, ReownAppKitModal } from '../tokens';
import { WalletAuthSessionStorageService } from './wallet-auth-session-storage.service';

const SIWX_AUTH_WAIT_TIMEOUT_MS = 120_000;
const SIWX_AUTH_POLL_INTERVAL_MS = 100;

@Injectable({ providedIn: 'root' })
export class WalletAuthorizationService {
  readonly #appKit = inject(REOWN_APPKIT_ACCESS);
  readonly #authSessionStorage = inject(WalletAuthSessionStorageService);
  readonly #walletIdentityStore = inject(WalletIdentityStore);

  isAuthorized(): boolean {
    return this.#walletIdentityStore.isConnected() && this.#authSessionStorage.hasSiwxAuthToken();
  }

  async ensureAuthorized(): Promise<boolean> {
    if (this.isAuthorized()) {
      return true;
    }

    if (this.#walletIdentityStore.isPending()) {
      return false;
    }

    await this.#walletIdentityStore.connect();

    const modal = this.#appKit.getCachedModal();

    if (!modal) {
      return this.isAuthorized();
    }

    return await this.#waitForAuthorization(modal);
  }

  #waitForAuthorization(modal: ReownAppKitModal | null): Promise<boolean> {
    if (!this.#walletIdentityStore.isConnected()) {
      return Promise.resolve(false);
    }

    if (this.#authSessionStorage.hasSiwxAuthToken()) {
      return Promise.resolve(true);
    }

    return new Promise(resolve => {
      let settled = false;
      let unsubscribeModal: (() => void) | null = null;

      const finish = (result: boolean): void => {
        if (settled) {
          return;
        }

        settled = true;
        globalThis.clearTimeout(timeoutId);
        globalThis.clearInterval(pollIntervalId);
        unsubscribeModal?.();
        resolve(result);
      };

      const resolveCurrentAuthorization = (): void => {
        finish(this.isAuthorized());
      };

      const timeoutId = globalThis.setTimeout(resolveCurrentAuthorization, SIWX_AUTH_WAIT_TIMEOUT_MS);
      const pollIntervalId = globalThis.setInterval(() => {
        if (this.#authSessionStorage.hasSiwxAuthToken() || !this.#walletIdentityStore.isConnected()) {
          resolveCurrentAuthorization();
        }
      }, SIWX_AUTH_POLL_INTERVAL_MS);

      unsubscribeModal =
        modal?.subscribeState(state => {
          if (!state.open) {
            resolveCurrentAuthorization();
          }
        }) ?? null;
    });
  }
}
